/**
 * RFQ Workflow Service
 *
 * API service for RFQ Workflow Agent operations.
 * Uses route-based email checking (no polling).
 */

import apiClient from '../utils/api';

const BASE_URL = '/api/rfq-workflow';

const rfqWorkflowService = {
  // ============================================================================
  // Workflow Operations
  // ============================================================================

  /**
   * Start a new RFQ workflow
   */
  startWorkflow: async (workflowId = 25, initialContext = {}) => {
    return apiClient.post(`${BASE_URL}/start`, {
      workflow_id: workflowId,
      initial_context: initialContext
    });
  },

  /**
   * Continue an existing workflow with user input
   */
  continueWorkflow: async (runId, userMessage, chatSessionId = null) => {
    return apiClient.post(`${BASE_URL}/continue`, {
      run_id: runId,
      user_message: userMessage,
      chat_session_id: chatSessionId
    });
  },

  /**
   * Get workflow run status
   */
  getWorkflowStatus: async (runId) => {
    return apiClient.get(`${BASE_URL}/status/${runId}`);
  },

  /**
   * List all workflow runs
   */
  listWorkflowRuns: async (workflowId = null, status = null, limit = 50) => {
    const params = { limit };
    if (workflowId) params.workflow_id = workflowId;
    if (status) params.status = status;
    return apiClient.get(`${BASE_URL}/runs`, { params });
  },

  // ============================================================================
  // RFQ Data Operations
  // ============================================================================

  /**
   * List RFQs for current user
   */
  listRFQs: async (status = null, limit = 50) => {
    const params = { limit };
    if (status) params.status = status;
    return apiClient.get(`${BASE_URL}/rfqs`, { params });
  },

  /**
   * Get RFQ details with items, conversations, and quotes
   */
  getRFQDetails: async (rfqId) => {
    return apiClient.get(`${BASE_URL}/rfq/${rfqId}`);
  },

  /**
   * Get all quotes for an RFQ
   */
  getRFQQuotes: async (rfqId) => {
    return apiClient.get(`${BASE_URL}/quotes/${rfqId}`);
  },

  // ============================================================================
  // Quote Management
  // ============================================================================

  /**
   * Save a vendor quote
   */
  saveQuote: async (quoteData) => {
    return apiClient.post(`${BASE_URL}/quote`, quoteData);
  },

  // ============================================================================
  // Email Operations (Route-based, NO polling)
  // ============================================================================

  /**
   * Check for new email replies from all vendors
   * User-triggered, no background polling
   */
  checkReplies: async (rfqId) => {
    return apiClient.post(`${BASE_URL}/check-replies/${rfqId}`);
  },

  /**
   * Get all vendor conversations for an RFQ
   */
  getConversations: async (rfqId) => {
    return apiClient.get(`${BASE_URL}/conversations/${rfqId}`);
  },

  /**
   * Get email thread for a specific conversation
   */
  getConversationEmails: async (conversationId) => {
    return apiClient.get(`${BASE_URL}/conversation/${conversationId}/emails`);
  },

  /**
   * Send a reply to a vendor
   */
  sendReply: async (conversationId, replyBody) => {
    return apiClient.post(`${BASE_URL}/conversation/${conversationId}/reply`, {
      reply_body: replyBody
    });
  },

  // ============================================================================
  // Analysis & PO Operations
  // ============================================================================

  /**
   * Run AI bid analysis
   */
  analyzeBids: async (rfqId) => {
    return apiClient.post(`${BASE_URL}/analyze`, { rfq_id: rfqId });
  },

  /**
   * Generate purchase orders
   */
  generatePurchaseOrders: async (rfqId, vendorId = null, itemVendorMap = null) => {
    const body = { rfq_id: rfqId };
    if (vendorId) body.vendor_id = vendorId;
    if (itemVendorMap) body.item_vendor_map = itemVendorMap;
    return apiClient.post(`${BASE_URL}/generate-po`, body);
  },

  /**
   * Submit PO to NetSuite
   */
  submitPOToNetSuite: async (poId) => {
    return apiClient.post(`${BASE_URL}/submit-po/${poId}`);
  },

  // ============================================================================
  // NetSuite Data
  // ============================================================================

  /**
   * Get subsidiaries from NetSuite
   */
  getSubsidiaries: async () => {
    return apiClient.get(`${BASE_URL}/netsuite/subsidiaries`);
  },

  /**
   * Get customers from NetSuite
   */
  getCustomers: async (subsidiaryId = null) => {
    const params = subsidiaryId ? { subsidiary_id: subsidiaryId } : {};
    return apiClient.get(`${BASE_URL}/netsuite/customers`, { params });
  },

  /**
   * Get items from NetSuite
   */
  getItems: async (search = null) => {
    const params = search ? { search } : {};
    return apiClient.get(`${BASE_URL}/netsuite/items`, { params });
  },

  /**
   * Get vendors from NetSuite
   */
  getVendors: async (search = null) => {
    const params = search ? { search } : {};
    return apiClient.get(`${BASE_URL}/netsuite/vendors`, { params });
  },

  /**
   * Get vendors that supply specific items
   */
  getVendorsForItems: async (itemIds) => {
    return apiClient.post(`${BASE_URL}/netsuite/vendors-for-items`, itemIds);
  }
};

export default rfqWorkflowService;
